import { useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useListStore } from '@/stores/useListStore';

export function useUrlSync() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const store = useListStore();

  // Sync URL to store on initial load
  useEffect(() => {
    // Get current filter values from URL
    const searchTerm = searchParams?.get('q') || '';
    const category = searchParams?.get('category') || undefined;
    const sortBy = searchParams?.get('sort') || 'date_desc';

    const priceMin = searchParams?.get('priceMin')
      ? parseFloat(searchParams.get('priceMin')!)
      : undefined;
    const priceMax = searchParams?.get('priceMax')
      ? parseFloat(searchParams.get('priceMax')!)
      : undefined;

    const leadsMin = searchParams?.get('leadsMin')
      ? parseFloat(searchParams.get('leadsMin')!)
      : undefined;
    const leadsMax = searchParams?.get('leadsMax')
      ? parseFloat(searchParams.get('leadsMax')!)
      : undefined;

    const containsEmails = searchParams?.get('containsEmails') === 'true';
    const containsVerifiedEmails = searchParams?.get('containsVerifiedEmails') === 'true';
    const containsPhones = searchParams?.get('containsPhones') === 'true';

    const lastUpdatedDays = searchParams?.get('lastUpdatedDays')
      ? parseInt(searchParams.get('lastUpdatedDays')!)
      : undefined;

    const page = searchParams?.get('page') ? parseInt(searchParams.get('page')!) : 1;

    // Update store with URL values
    store.setSearchTerm(searchTerm);
    store.setCategory(category);
    store.setSortBy(sortBy);
    store.setPriceRange(priceMin, priceMax);
    store.setLeadsRange(leadsMin, leadsMax);
    store.setContainsEmails(containsEmails);
    store.setContainsVerifiedEmails(containsVerifiedEmails);
    store.setContainsPhones(containsPhones);
    store.setLastUpdatedDays(lastUpdatedDays);
    store.setCurrentPage(page);
  }, [searchParams]);

  // Sync store to URL when store changes
  useEffect(() => {
    const handleStoreChange = () => {
      const state = useListStore.getState();
      const params = new URLSearchParams(searchParams?.toString() || '');

      // Update URL params based on store state
      if (state.searchTerm) params.set('q', state.searchTerm);
      else params.delete('q');

      if (state.category) params.set('category', state.category);
      else params.delete('category');

      if (state.sortBy) params.set('sort', state.sortBy);
      else params.delete('sort');

      if (state.priceRange.min !== undefined)
        params.set('priceMin', state.priceRange.min.toString());
      else params.delete('priceMin');

      if (state.priceRange.max !== undefined)
        params.set('priceMax', state.priceRange.max.toString());
      else params.delete('priceMax');

      if (state.leadsRange.min !== undefined)
        params.set('leadsMin', state.leadsRange.min.toString());
      else params.delete('leadsMin');

      if (state.leadsRange.max !== undefined)
        params.set('leadsMax', state.leadsRange.max.toString());
      else params.delete('leadsMax');

      if (state.containsEmails) params.set('containsEmails', 'true');
      else params.delete('containsEmails');

      if (state.containsVerifiedEmails) params.set('containsVerifiedEmails', 'true');
      else params.delete('containsVerifiedEmails');

      if (state.containsPhones) params.set('containsPhones', 'true');
      else params.delete('containsPhones');

      if (state.lastUpdatedDays !== undefined)
        params.set('lastUpdatedDays', state.lastUpdatedDays.toString());
      else params.delete('lastUpdatedDays');

      if (state.currentPage > 1) params.set('page', state.currentPage.toString());
      else params.delete('page');

      // Update URL without causing a page reload
      router.push(`?${params.toString()}`, { scroll: false });
    };

    // Subscribe to store changes
    const unsubscribe = useListStore.subscribe(handleStoreChange);

    // Cleanup subscription
    return () => unsubscribe();
  }, [router, searchParams]);
}
